import React, { useState, useEffect } from 'react';
import './App.css';
import Sidebar from './components/Sidebar';
import VideoGenerator from './components/VideoGenerator';
import JobsList from './components/JobsList';
import CharacterGenerator from './components/CharacterGenerator';
import CharacterLibrary from './components/CharacterLibrary';
import CharacterEdit from './components/CharacterEdit';
import EnvironmentGenerator from './components/EnvironmentGenerator';
import EnvironmentLibrary from './components/EnvironmentLibrary';
import EnvironmentEdit from './components/EnvironmentEdit';
import InfluencerPrompt from './components/InfluencerPrompt';
import Profile from './components/Profile';
import Login from './components/Login';
import AdminPanel from './components/AdminPanel';
import { useAuth } from './context/AuthContext';
import { videoService } from './services/api';

function App() {
  const { user, loading, login, logout, isAdmin, isAuthenticated } = useAuth();
  const [activeView, setActiveView] = useState('generate');
  const [jobs, setJobs] = useState({});
  const [dimensions, setDimensions] = useState([]);
  const [editData, setEditData] = useState(null);

  // Navigation handler for AI Influencer components
  const handleNavigate = (view, data) => {
    setActiveView(view);
    if (data) {
      setEditData(data);
    } else {
      setEditData(null);
    }
  };

  // Poll for job updates every 3 seconds
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const jobsData = await videoService.getAllJobs();
        setJobs(jobsData);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      }
    };

    // Initial fetch
    fetchJobs();

    // Set up polling
    const interval = setInterval(fetchJobs, 3000);

    return () => clearInterval(interval);
  }, []);

  // Fetch available dimensions on mount
  useEffect(() => {
    const fetchDimensions = async () => {
      try {
        const data = await videoService.getDimensions();
        setDimensions(data.available_dimensions || []);
      } catch (error) {
        console.error('Error fetching dimensions:', error);
      }
    };

    fetchDimensions();
  }, []);

  const handleGenerateVideo = async (prompt, dimension, duration) => {
    try {
      const result = await videoService.generateVideo(prompt, dimension, duration);
      
      // Add the new job to our jobs list immediately
      setJobs(prev => ({
        ...prev,
        [result.job_id]: {
          status: 'processing',
          progress: 0,
          message: 'Job queued for processing',
          created_at: new Date().toISOString()
        }
      }));

      return result;
    } catch (error) {
      console.error('Error generating video:', error);
      throw error;
    }
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await videoService.deleteJob(jobId);
      setJobs(prev => {
        const newJobs = { ...prev };
        delete newJobs[jobId];
        return newJobs;
      });
    } catch (error) {
      console.error('Error deleting job:', error);
      throw error;
    }
  };

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner">
          <i className="fas fa-spinner fa-spin"></i>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={login} />;
  }

  return (
    <div className="app">
      <Sidebar
        activeView={activeView}
        setActiveView={(view) => handleNavigate(view)}
        onLogout={logout}
        user={user}
        isAdmin={isAdmin}
      />
      <div className="main-content">
        {activeView === 'generate' && (
          <VideoGenerator 
            onGenerate={handleGenerateVideo}
            dimensions={dimensions}
          />
        )}
        {activeView === 'jobs' && (
          <JobsList 
            jobs={jobs}
            onDeleteJob={handleDeleteJob}
          />
        )}
        {activeView === 'influencer-prompt' && (
          <InfluencerPrompt onNavigate={handleNavigate} />
        )}
        {activeView === 'character-generator' && (
          <CharacterGenerator onNavigate={handleNavigate} />
        )}
        {activeView === 'character-library' && (
          <CharacterLibrary onNavigate={handleNavigate} />
        )}
        {activeView === 'character-edit' && (
          <CharacterEdit editData={editData} onNavigate={handleNavigate} />
        )}
        {activeView === 'environment-generator' && (
          <EnvironmentGenerator onNavigate={handleNavigate} />
        )}
        {activeView === 'environment-library' && (
          <EnvironmentLibrary onNavigate={handleNavigate} />
        )}
        {activeView === 'environment-edit' && (
          <EnvironmentEdit editData={editData} onNavigate={handleNavigate} />
        )}
        {activeView === 'profile' && (
          <Profile />
        )}
        {activeView === 'admin' && isAdmin && (
          <AdminPanel />
        )}
      </div>
    </div>
  );
}

export default App;
